import { useAuth } from "@clerk/react";
import { ManagedRelay, setManagedRelaySession } from "@cleo/client-runtime/relay";
import {
  reportAtomCommandResult,
  settleAsyncResult,
  settlePromise,
} from "@cleo/client-runtime/state/runtime";
import * as Effect from "effect/Effect";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { environmentCatalog } from "../connection/catalog";
import { runtime } from "../lib/runtime";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { useAtomCommand } from "../state/use-atom-command";
import { resolveRelayClerkTokenOptions } from "./publicConfig";

export type ManagedAccountTransition =
  | { readonly phase: "checking" }
  | { readonly phase: "ready" }
  | { readonly phase: "failed"; readonly retry: () => void; readonly signOut: () => void };

const ManagedAccountTransitionContext = createContext<ManagedAccountTransition>({ phase: "ready" });

export function useManagedAccountTransition(): ManagedAccountTransition {
  return useContext(ManagedAccountTransitionContext);
}

let relayTokenProvider: (() => Promise<string | null>) | null = null;

export async function readManagedRelayClerkToken(): Promise<string | null> {
  return relayTokenProvider?.() ?? null;
}

export function deactivateManagedRelayAuthentication(): void {
  relayTokenProvider = null;
  setManagedRelaySession(appAtomRegistry, null);
}

export function activateManagedRelayAuthentication(
  accountId: string,
  readClerkToken: () => Promise<string | null>,
): void {
  relayTokenProvider = readClerkToken;
  setManagedRelaySession(appAtomRegistry, {
    accountId,
    readClerkToken,
  });
}

type TransitionState =
  | { readonly phase: "checking" | "ready" }
  | { readonly phase: "failed"; readonly retry: () => void };

export function ManagedRelayAuthProvider({ children }: { readonly children: ReactNode }) {
  const { getToken, isLoaded, isSignedIn, signOut, userId } = useAuth({
    treatPendingAsSignedOut: false,
  });
  const removeRelayEnvironments = useAtomCommand(environmentCatalog.removeRelayEnvironments, {
    reportFailure: false,
    reportDefect: false,
  });
  const observedAccountRef = useRef<string | null | undefined>(undefined);
  // Resolves true only when the previous account's cleanup fully succeeded; it
  // never rejects, so a failed cleanup cannot poison the next transition.
  const accountTransitionRef = useRef<Promise<boolean> | null>(null);
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const [state, setState] = useState<TransitionState>({ phase: "ready" });

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    let cancelled = false;
    const previousAccount = observedAccountRef.current;
    const nextAccount = isSignedIn && userId ? userId : null;
    observedAccountRef.current = nextAccount;

    // PC6: the account's data is cleared only when BOTH the relay
    // environments and the relay token cache are gone.
    const queueAccountCleanup = (): Promise<boolean> => {
      const previousTransition = accountTransitionRef.current ?? Promise.resolve(true);
      const next = previousTransition.then(async () => {
        const results = await Promise.all([
          removeRelayEnvironments(),
          settleAsyncResult(() =>
            runtime.runPromiseExit(
              ManagedRelay.ManagedRelayClient.pipe(
                Effect.flatMap((client) => client.resetTokenCache),
              ),
            ),
          ),
        ]);
        for (const result of results) {
          reportAtomCommandResult(result, { label: "cloud account cleanup" });
        }
        return results.every((result) => AsyncResult.isSuccess(result));
      });
      accountTransitionRef.current = next.catch(() => false);
      return accountTransitionRef.current;
    };

    if (!isSignedIn || !userId) {
      deactivateManagedRelayAuthentication();
      setState({ phase: "ready" });
      if (previousAccount !== null) {
        void queueAccountCleanup();
      }
    } else {
      const tokenProvider = () => getToken(resolveRelayClerkTokenOptions());
      const activateWhenCleanupSucceeds = (transition: Promise<boolean>) => {
        void (async () => {
          const result = await settlePromise(async () => {
            const cleanedUp = await transition;
            if (cancelled) {
              return;
            }
            if (cleanedUp) {
              activateManagedRelayAuthentication(userId, tokenProvider);
              setState({ phase: "ready" });
              return;
            }
            deactivateManagedRelayAuthentication();
            setState({
              phase: "failed",
              retry: () => {
                if (cancelled) {
                  return;
                }
                setState({ phase: "checking" });
                activateWhenCleanupSucceeds(queueAccountCleanup());
              },
            });
          });
          reportAtomCommandResult(result, { label: "cloud account activation" });
        })();
      };
      if (previousAccount !== undefined && previousAccount !== null && previousAccount !== userId) {
        deactivateManagedRelayAuthentication();
        setState({ phase: "checking" });
        activateWhenCleanupSucceeds(queueAccountCleanup());
      } else {
        activateWhenCleanupSucceeds(accountTransitionRef.current ?? Promise.resolve(true));
      }
    }
    return () => {
      cancelled = true;
    };
  }, [getToken, isLoaded, isSignedIn, removeRelayEnvironments, userId]);

  useEffect(() => () => deactivateManagedRelayAuthentication(), []);

  const transition = useMemo<ManagedAccountTransition>(
    () =>
      state.phase === "failed"
        ? {
            phase: "failed",
            retry: state.retry,
            signOut: () => void settlePromise(async () => signOutRef.current()),
          }
        : { phase: state.phase },
    [state],
  );

  return (
    <ManagedAccountTransitionContext.Provider value={transition}>
      {children}
    </ManagedAccountTransitionContext.Provider>
  );
}
